import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Alert, Spinner, Table } from "react-bootstrap";
import EmployeeSearchAdmin from "../../common/empsearch/EmployeeSearchAdmin";
import { getPtoDetails_Admin } from "../api/PtoDetailsApi";
import Information from "../../common/alerts/Information";
import { useParams, useHistory } from "react-router-dom";
import Decimal from "../../common/typeFormatting/DecimalFmt";
import DataHeader, { DataItem } from "../../common/dataheader/dataheader";
import "./AdminStyles.css";

const AdminEmployeeDetails = () => {
  const { employeeId } = useParams();
  const history = useHistory();
  
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [ptoDetails, setPtoDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Effect to load the initial employee from the route parameter
  useEffect(() => {
    if (employeeId && !selectedEmployee) {
      setSelectedEmployee({
        employeeId: employeeId,
      });
    }
  }, [employeeId, selectedEmployee]);

  // Effect to load PTO details when selected employee changes
  useEffect(() => {
    async function fetchPtoDetails() {
      if (!selectedEmployee || !selectedEmployee.employeeId) {
        return;
      }

      setIsLoading(true);
      setError(null);
      
      try {
        const details = await getPtoDetails_Admin(selectedEmployee.employeeId);
        setPtoDetails(details);
      } catch (err) {
        console.error("Error fetching PTO details:", err);
        setError("Failed to load PTO details. Please try again.");
      } finally {
        setIsLoading(false);
      }
    }

    fetchPtoDetails();
  }, [selectedEmployee]);

  const handleEmployeeChange = (employee) => {
    setSelectedEmployee(employee);
    history.push(`/pto/admin/employee/${employee.employeeId}`);
  };

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col md={6}>
          <Card style={{ marginTop: 25 }}>
            <Card.Body>
              <Card.Title>Employee Search</Card.Title>
              <Row>
                <Col>
                  <EmployeeSearchAdmin
                    onSelect={handleEmployeeChange}
                    preselectedEmployee={selectedEmployee}
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
        
      {error && (
        <Row>
          <Col>
            <Alert variant="danger">{error}</Alert>
          </Col>
        </Row>
      )}

      {isLoading && selectedEmployee ? (
        <Row>
          <Col className="text-center py-5">
            <Spinner animation="border" />
            <p className="mt-3">Loading PTO details...</p>
          </Col>
        </Row>
      ) : ptoDetails ? (
        <>
          <DataHeader 
            title={`${ptoDetails.firstName || ''} ${ptoDetails.lastName || ''}'s PTO Summary`}
            align="center"
          >
            <DataItem 
              label="Available Hours"
              value={<Decimal>{ptoDetails.totals.availableHours}</Decimal>}
            />
            <DataItem 
              label="YTD Earned"
              value={<Decimal>{ptoDetails.totals.earnedHours}</Decimal>}
            />
            <DataItem 
              label="YTD Used"
              value={<Decimal>{ptoDetails.totals.usedHours}</Decimal>}
            />
          </DataHeader>

          <Card>
            <Card.Body>
              <Table className="table table-hover">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th className='App-number'>Earned</th>
                    <th>-</th>
                    <th className='App-number'>Used</th>
                    <th>=</th>
                    <th className='App-number'>Net Earned</th>
                    <th className='App-number'>End Of Month Balance</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{ptoDetails.yearRolloverBalance.year} Rollover</td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td className='App-number'><Decimal>{ptoDetails.yearRolloverBalance.rolloverBalance}</Decimal></td>
                  </tr>
                  {ptoDetails.monthDetails.map((item) => (
                    <tr key={item.empPtoDetailsId.toString()}>
                      <td>{item.monthName}</td>
                      <td className='App-number'><Decimal>{item.earnedHours}</Decimal></td>
                      <td></td>
                      <td className='App-number'><Decimal>{item.usedHours}</Decimal></td>
                      <td></td>
                      <td className='App-number'><Decimal>{item.netEarned}</Decimal></td>
                      <td className='App-number'><Decimal>{item.endOfMonthBalance}</Decimal></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className='App-footer'>
                  <tr>
                    <td>YTD Totals</td>
                    <td className='App-number'><Decimal>{ptoDetails.totals.earnedHours}</Decimal></td>
                    <td></td>
                    <td className='App-number'><Decimal>{ptoDetails.totals.usedHours}</Decimal></td>
                    <td></td>
                    <td className='App-number'><Decimal>{ptoDetails.totals.netEarnedHours}</Decimal></td>
                    <td className='App-number'><Decimal>{ptoDetails.totals.availableHours}</Decimal></td>
                  </tr>
                </tfoot>
              </Table>
              <Information title="Notes">
                <div style={{ marginBottom: "8px" }}>
                  Pending PTO Requests logged in the PTO App on the Connect portal are not included.
                </div>
                <div style={{ marginBottom: "8px" }}>
                  PTO is accrued monthly on the last paycheck of every month.
                </div>
                <div style={{ marginBottom: "8px" }}>
                  Used hours are as of last time card.
                </div>
              </Information>
            </Card.Body>
          </Card>
        </>
      ) : (
        selectedEmployee && (
          <Row>
            <Col>
              <Alert variant="info">
                Select an employee to view their PTO details.
              </Alert>
            </Col>
          </Row>
        )
      )}
    </Container>
  );
};

export default AdminEmployeeDetails;